import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
// aria-label: design-system-bypass 

import { Navigate } from 'react-router-dom';
// aria-label: design-system-bypass 

import { authService, User, UserRole } from '../services/authService';
// aria-label: design-system-bypass 


interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (mobile: string) => Promise<boolean>;
    devLogin: (role: UserRole) => Promise<void>;
    logout: () => Promise<void>;
    refreshUser: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Check for existing session on mount
    useEffect(() => {
        const session = authService.getSession();
        if (session) {
            setUser(session);
        }
        setIsLoading(false);

        // Listen for storage events (e.g. from PricingPage or other tabs)
        const handleStorageChange = () => {
            const updatedSession = authService.getSession();
            setUser(updatedSession);
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    const refreshUser = () => {
        const session = authService.getSession();
        setUser(session);
    };

    const login = async (mobile: string): Promise<boolean> => {
        setIsLoading(true);
        try {
            const result = await authService.loginWithMobile(mobile);
            if (result.success && result.user) {
                setUser(result.user);
                return true;
            }
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const devLogin = async (role: UserRole): Promise<void> => {
        setIsLoading(true);
        try {
            const loggedInUser = await authService.devLogin(role);
            setUser(loggedInUser);
        } finally {
            setIsLoading(false);
        }
    };

    const logout = async () => {
        await authService.logout();
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{
            user,
            isAuthenticated: !!user,
            isLoading,
            login,
            devLogin,
            logout,
            refreshUser
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

// Protected Route Component
export function ProtectedRoute({
    children,
    allowedRoles
}: {
    children: ReactNode;
    allowedRoles?: UserRole[];
}) {
    const { user, isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
        return (
        <div aria-label="Page Container" className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        // Redirect to login using React Router
        return <Navigate to="/login" replace />;
    }

    if (allowedRoles && user && !allowedRoles.includes(user.role)) {
        // Redirect to user's appropriate dashboard
        return <Navigate to={authService.getRedirectPath(user.role)} replace />;
    }

    return <>{children}</>;
}
